import React, {FunctionComponent, Fragment} from 'react'
import {Listbox, Transition} from '@headlessui/react'
import {CheckIcon, SelectorIcon} from '@heroicons/react/solid'
import {useQuery} from "@apollo/client";
import {GET_VEHICLETREE} from "@App/graphql/queries";
import {useDispatch, useSelector} from "react-redux";
import {actionCreators, State} from "@App/state";
import {bindActionCreators} from "@reduxjs/toolkit";
import {Brand} from "@App/types";

type BrandFilterProps = {
    onSubmit?: (brand: Brand) => void
}

export const BrandFilter: FunctionComponent<BrandFilterProps> = ({onSubmit}: BrandFilterProps) => {
    const brand = useSelector((state: State) => state.filters.brand)
    const dispatch = useDispatch();
    const {changeBrandFilter} = bindActionCreators(actionCreators, dispatch)

    const {data} = useQuery(GET_VEHICLETREE, {
        variables: {
            ...organization && {organization: +organization.id},
            includeGenerations: false,
            includeMotorizations: false
        }
    })

    // set brand on change
    const onChange = (e) => {
        changeBrandFilter(e)
        onSubmit(e)
    };

    const getResetListboxOption = () => {
        return (
            <Listbox.Option
                key={null}
                className={({active}) =>
                    `relative select-none z-10 py-2 pl-10 pr-4 ${
                        active ? 'bg-primary-100 text-white' : 'text-gray-900'
                    }`
                }
                value={null}>
                {({selected}) => (
                    <div>
                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                        Marke auswählen
                    </span>
                        {selected ? (
                            <span
                                className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-600">
                            <CheckIcon className="h-5 w-5" aria-hidden="true"/>
                        </span>
                        ) : null}
                    </div>
                )}
            </Listbox.Option>
        )
    }

    const getBrands = () => {
        return (data && data.brands.map(brand => (
            <Listbox.Option
                key={brand.id}
                className={({active}) =>
                    `relative select-none z-10 py-2 pl-10 pr-4 ${
                        active ? 'bg-primary-100 text-white' : 'text-gray-900'
                    }`
                }
                value={brand}
            >
                {({active, selected}) => (
                    <div>
                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                            {brand.name}
                        </span>
                        {selected ? (
                            <span
                                className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-600">
                              <CheckIcon className="h-5 w-5" aria-hidden="true"/>
                            </span>
                        ) : null}
                    </div>
                )}
            </Listbox.Option>
        )))
    }

    return (
        <div className="relative">
            <div>
                <div className={`relative md:w-80 max-w-full`}>
                    <div className="top-16">
                        <Listbox value={brand} onChange={(value) => onChange(value)}>
                            <div className="relative mt-1">
                                <Listbox.Button
                                    className="relative w-full rounded bg-white py-2 px-3 text-left shadow-md border focus:border-primary border-neutral-500 focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm">
                                    {brand && (
                                        <span className="block truncate text-base text-neutral-900">
                                            {brand.name}
                                        </span>
                                    )}
                                    {!brand && (
                                        <span className="block truncate text-base text-neutral-500">
                                            Marke auswählen
                                        </span>
                                    )}
                                    <span
                                        className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                      <SelectorIcon
                                          className="h-5 w-5 text-gray-400"
                                          aria-hidden="true"
                                      />
                                    </span>
                                </Listbox.Button>
                                <Transition
                                    as={Fragment}
                                    leave="transition ease-in duration-100"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                >
                                    <Listbox.Options
                                        className="absolute mt-1 max-h-60 w-full overflow-auto rounded bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-10">
                                        {getResetListboxOption()}
                                        {getBrands()}
                                    </Listbox.Options>
                                </Transition>
                            </div>
                        </Listbox>
                    </div>
                </div>
            </div>
        </div>
    )
}
